import { api } from "../api";
import { el } from "../lib/dom";

/** Floating bottom-right "Ask Hub" chat. Mounted once on <body>; persists across
 *  routes. Claude-backed (POST /api/assistant/ask), grounded on the whole catalogue —
 *  recommends apps, explains what they do, answers factual questions, and says
 *  "I don't know" when the answer isn't in the catalogue. */
const STARTERS = [
  "Which app analyzes the Salesforce pipeline?",
  "Does Kautilya have a demo video?",
  "Which app tracks AI coding cost?",
];

export function renderAskHubFab(): HTMLElement {
  const messages = el("div", { class: "ask-messages" });
  const input = el("textarea", { class: "ask-input", rows: "1", placeholder: "Ask about an app…" });
  const send = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["Ask"]);

  const scrollDown = () => {
    messages.scrollTop = messages.scrollHeight;
  };

  function bubble(role: "user" | "bot", ...children: (Node | string)[]): HTMLElement {
    return el("div", { class: `ask-msg ask-msg-${role}` }, children);
  }

  const history: { role: string; content: string }[] = [];
  let busy = false;
  async function run(query: string) {
    query = query.trim();
    if (!query || busy) return;
    busy = true;
    input.value = "";
    starters.remove();
    messages.append(bubble("user", query));
    const answer = el("span", { class: "muted-sm" }, ["Thinking…"]);
    messages.append(bubble("bot", answer));
    scrollDown();
    try {
      const res = await api.askHub(query, history);
      answer.className = "";
      answer.textContent = res.answer;
      history.push({ role: "user", content: query }, { role: "assistant", content: res.answer });
    } catch {
      answer.textContent = "Sorry — I couldn't reach the assistant right now.";
    } finally {
      busy = false;
      scrollDown();
      input.focus();
    }
  }

  send.addEventListener("click", () => void run(input.value));
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void run(input.value);
    }
  });

  const starters = el(
    "div",
    { class: "ask-starters" },
    STARTERS.map((s) => {
      const chip = el("button", { class: "ask-starter", type: "button" }, [s]);
      chip.addEventListener("click", () => void run(s));
      return chip;
    }),
  );

  const panel = el(
    "div",
    { class: "askfab-panel", hidden: "", role: "dialog", "aria-label": "Ask Hub" },
    [
      el("div", { class: "ask-head" }, [
        el("span", { class: "ask-title grad-text" }, ["Ask Hub"]),
        el("span", { class: "muted-sm" }, ["Ask about any app, or what fits your task."]),
      ]),
      messages,
      starters,
      el("div", { class: "ask-compose" }, [input, send]),
    ],
  );

  const toggle = el(
    "button",
    { class: "askfab-toggle", type: "button", "aria-expanded": "false" },
    ["Ask Hub"],
  );
  toggle.addEventListener("click", () => {
    const opening = panel.hasAttribute("hidden");
    panel.toggleAttribute("hidden", !opening);
    toggle.setAttribute("aria-expanded", String(opening));
    if (opening) input.focus();
  });

  return el("div", { class: "askfab" }, [panel, toggle]);
}
